import { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft, User, Mail, Phone, MapPin, Calendar, GraduationCap, Send } from 'lucide-react'
import { sendFormEmail } from '../utils/email'

const RegisterPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const programName = location.state?.programName || ''

  const programs = [
    { id: 'cosmos', name: 'Cosmos Juniors (Ages 6-8)' },
    { id: 'titans', name: 'Titans (Ages 9-15)' },
    { id: 'warriors', name: 'Warriors (Ages 9-15)' },
    { id: 'lakers', name: 'Lakers (Ages 9-15)' },
  ]

  const [formData, setFormData] = useState({
    parentName: '',
    email: '',
    phone: '',
    location: '',
    kidsAge: '',
    kidsGrade: '',
    selectedProgram: programName || 'titans'
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus('idle')

    try {
      const selectedProgram = programs.find(p => p.id === formData.selectedProgram)
      await sendFormEmail({
        source: 'Register Page',
        parentName: formData.parentName,
        email: formData.email,
        phone: formData.phone,
        selectedProgram: selectedProgram?.name || formData.selectedProgram,
        childAge: formData.kidsAge,
        message: `Location: ${formData.location}\nGrade: ${formData.kidsGrade}`,
      })
      setSubmitStatus('success')
      
      // Reset form after successful submission
      setTimeout(() => {
        navigate('/programs')
      }, 2000)
    } catch (error) {
      console.error('Registration error:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Programs
        </motion.button>

        {/* Form Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 px-8 py-12 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <User className="h-10 w-10 text-blue-600" />
            </motion.div>
            <h1 className="text-3xl font-bold text-white mb-2">
              Pre - Register for STEM BEARS Programs
            </h1>
            <p className="text-blue-100">
              Fill out the form below to get started
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            {/* Program Selection */}
            <div>
              <label className="flex items-center text-gray-700 font-medium mb-2">
                <GraduationCap className="h-5 w-5 mr-2 text-blue-600" />
                Select Program *
              </label>
              <select
                name="selectedProgram"
                value={formData.selectedProgram}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
              >
                {programs.map((program) => (
                  <option key={program.id} value={program.id}>
                    {program.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Parent Name */}
            <div>
              <label className="flex items-center text-gray-700 font-medium mb-2">
                <User className="h-5 w-5 mr-2 text-blue-600" />
                Parent/Guardian Name *
              </label>
              <input
                type="text"
                name="parentName"
                value={formData.parentName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="Enter parent or guardian name"
              />
            </div>

            {/* Email */}
            <div>
              <label className="flex items-center text-gray-700 font-medium mb-2">
                <Mail className="h-5 w-5 mr-2 text-blue-600" />
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="your.email@example.com"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="flex items-center text-gray-700 font-medium mb-2">
                <Phone className="h-5 w-5 mr-2 text-blue-600" />
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="(555) 123-4567"
              />
            </div>

            {/* Location */}
            <div>
              <label className="flex items-center text-gray-700 font-medium mb-2">
                <MapPin className="h-5 w-5 mr-2 text-blue-600" />
                Location *
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="City, State"
              />
            </div>

            {/* Kids Age */}
            <div>
              <label className="flex items-center text-gray-700 font-medium mb-2">
                <Calendar className="h-5 w-5 mr-2 text-blue-600" />
                Student's Age *
              </label>
              <input
                type="number"
                name="kidsAge"
                value={formData.kidsAge}
                onChange={handleChange}
                required
                min="5"
                max="18"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="Age"
              />
            </div>

            {/* Kids Grade */}
            <div>
              <label className="flex items-center text-gray-700 font-medium mb-2">
                <GraduationCap className="h-5 w-5 mr-2 text-blue-600" />
                Student's Grade Level *
              </label>
              <select
                name="kidsGrade"
                value={formData.kidsGrade}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              >
                <option value="">Select grade level</option>
                <option value="Elementary - K-5">Elementary (K-5)</option>
                <option value="Middle School - 6-8">Middle School (6-8)</option>
                <option value="High School - 9-12">High School (9-12)</option>
              </select>
            </div>

            {/* Success Message */}
            {submitStatus === 'success' && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-green-50 border border-green-200 rounded-lg p-4"
              >
                <p className="text-green-800 font-medium">
                  ✓ Registration successful! Redirecting...
                </p>
              </motion.div>
            )}

            {/* Error Message */}
            {submitStatus === 'error' && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border border-red-200 rounded-lg p-4"
              >
                <p className="text-red-800 font-medium">
                  ✗ Something went wrong. Please try again.
                </p>
              </motion.div>
            )}

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white font-semibold py-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-5 w-5 mr-2" />
                  Submit Registration
                </>
              )}
            </motion.button>
          </form>
        </motion.div>

      </div>
    </div>
  )
}

export default RegisterPage
